/** @jsxImportSource @emotion/react */
// import { jsx } from '@emotion/react';
import { useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { IUploadRequest } from '../../../@types/api';
import { AppToken } from '../../../App';
import { ReactComponent as UploadIcon } from '../../../assets/images/upload_2_line.svg';
import { Spinner, notify } from '../../../components';
import {
  DEFAULT_ACCEPT_FILE_EXTENSIONS,
  MAX_FILE_ALLOWED,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_IN_MB,
  PATTERNS,
  VALID_FILE_EXTENSIONS,
} from '../../../constants';
import { COLORS } from '../../../styles/colors';
import { FONT_WEIGHT } from '../../../styles/fonts';
import { updloadTargetFiles } from '../../../utils/apiHelper';
import Text from '../Text';

const maxLength = 100;

function FileBox({
  folderId,
  // setFolderId,
  folderName,
  setIsInit,
}: {
  folderId: number | null;
  setFolderId?: (folderId: number | null) => void;
  folderName?: string | '';
  setIsInit: (inIt: boolean) => void;
}) {
  const { authorizedToken } = JSON.parse(useContext(AppToken));
  const [isShowSpinner, setIsShowSpinner] = useState(false);
  const [fileError, setFileError] = useState('');
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (files, rejectedFiles) => {
      if (files && rejectedFiles.length === 0) {
        const uploadRequest: IUploadRequest = {
          folderId: folderId,
          files: files,
        };
        setIsShowSpinner(true);
        const { errorMessage, response } = await updloadTargetFiles(
          uploadRequest,
          authorizedToken,
        );

        if (errorMessage) {
          notify({
            message: errorMessage ?? 'Error while uploading file',
            type: 'error',
          });
          setIsShowSpinner(false);
          return;
        }

        if (response) {
          notify({
            message: response.message || 'File uploaded successfully',
            type: 'success',
          });
          setIsShowSpinner(false);
          setIsInit(true);
          // setFolderId(null);
        }
        setFileError('');
      } else {
        rejectedFiles.map((file) => {
          file.errors.map((error) => {
            if (error.message.startsWith('File type')) {
              setFileError(
                `${file.file.name} upload failed. File type is not valid`,
              );
            } else if (error.message.startsWith('File size')) {
              setFileError(
                `${file.file.name} upload failed. File size is larger than 200MB`,
              );
            } else if (error.message.startsWith('Too many files')) {
              setFileError(
                `Too many files, upload failed. Max ${MAX_FILE_ALLOWED} files allowed`,
              );
            } else {
              setFileError(error.message);
            }
            return null;
          });
        });
      }
    },
    accept: DEFAULT_ACCEPT_FILE_EXTENSIONS,
    maxFiles: MAX_FILE_ALLOWED,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    validator: function (_file) {
      if (_file.name.length > maxLength) {
        return {
          code: 'name-too-large',
          message: `File name is larger than ${maxLength} characters`,
        };
      }
      if (!PATTERNS.FILE_NAME.test(_file.name)) {
        return {
          code: 'name-invalid',
          message: `File ${_file.name} contain invalid character. Please do not include blank space and special characters  * | < > [ ] { } " ' ; @ $ # % ^ ~`,
        };
      }
      return null;
    },
  });

  return (
    <>
      {isShowSpinner ? (
        <Spinner
          commonStyles={{
            alignItems: 'center',
            flex: 1,
          }}
        />
      ) : null}
      <div
        style={{
          border: `1px solid ${COLORS.grey8}`,
          borderRadius: '4px',
          marginBottom: '1rem',
          background: `${COLORS.grey1}`,
        }}
      >
        <div
          style={{
            border: `1px solid ${COLORS.grey8}`,
            borderRadius: '4px',
            marginTop: '1rem',
            marginBottom: '1rem',
            margin: '1rem',
            background: `${COLORS.grey1}`,
            gap: '4px',
            padding: '0 1rem',
          }}
        >
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '2rem 0 2rem 0',
              }}
            >
              <UploadIcon />
              <Text
                commonStyles={{
                  weight: FONT_WEIGHT.bold,
                  size: '14px',
                  color: COLORS.blue3,
                  alignItems: 'center',
                }}
              >
                Click or drop files here to upload to 'S:\
                {folderName ? folderName : ''}'
              </Text>
              <Text
                commonStyles={{
                  weight: FONT_WEIGHT.regular,
                  size: '14px',
                  color: COLORS.black2,
                }}
              >
                Accepts {VALID_FILE_EXTENSIONS}
              </Text>
              <Text
                commonStyles={{
                  weight: FONT_WEIGHT.regular,
                  size: '14px',
                  color: COLORS.black2,
                }}
              >
                Max {MAX_FILE_ALLOWED} files of {MAX_FILE_SIZE_IN_MB} allowed
              </Text>
            </div>
          </div>
        </div>
        {fileError ? (
          <div
            css={{ padding: '0 1rem', marginTop: '0rem', marginBottom: '1rem' }}
          >
            <Text
              commonStyles={{
                weight: FONT_WEIGHT.regular,
                size: '14px',
                color: COLORS.red,
              }}
            >
              {/* <span
              css={{
                fontSize: '0.75rem',
                color: COLORS.red,
              }}
            > */}
              {fileError}
              {/* </span> */}
            </Text>
          </div>
        ) : null}
      </div>
    </>
  );
}
export default FileBox;
